import { memo } from 'react';
import { Download, ChevronLeft, ChevronRight, ArrowUpDown } from 'lucide-react';
import { Button } from './ui/button';
import { SortOption, SortDirection } from '../utils/vendorUtils';

interface VendorToolbarProps {
  // Sorting
  sortBy: SortOption;
  sortDirection: SortDirection;
  onSortChange: (sortBy: SortOption) => void;
  onDirectionToggle: () => void;
  
  // Pagination
  currentPage: number;
  totalPages: number;
  totalVendors: number;
  onPageChange: (page: number) => void;
  
  // Export
  onExportCSV: () => void;
}

function VendorToolbar({
  sortBy,
  sortDirection,
  onSortChange,
  onDirectionToggle,
  currentPage,
  totalPages,
  totalVendors,
  onPageChange,
  onExportCSV,
}: VendorToolbarProps) {
  const sortOptions: { value: SortOption; label: string }[] = [
    { value: 'name', label: 'Name' },
    { value: 'rating', label: 'Rating' },
    { value: 'jobs', label: 'Jobs' },
    { value: 'date', label: 'Date' },
  ];

  const startIndex = (currentPage - 1) * 20 + 1;
  const endIndex = Math.min(currentPage * 20, totalVendors);

  return (
    <div className="flex items-center justify-between bg-gray-50 rounded-lg p-4">
      {/* Sort Controls */}
      <div className="flex items-center space-x-2">
        <span className="text-sm font-medium text-gray-700">Sort by:</span>
        <select
          aria-label="Sort by"
          value={sortBy}
          onChange={(e) => onSortChange(e.target.value as SortOption)}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        >
          {sortOptions.map(option => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <Button
          variant="outline"
          size="sm"
          onClick={onDirectionToggle}
          className="flex items-center space-x-1"
        >
          <ArrowUpDown className="h-4 w-4" />
          <span>{sortDirection === 'asc' ? 'Asc' : 'Desc'}</span>
        </Button>
      </div>

      {/* Pagination Controls */}
      <div className="flex items-center space-x-4">
        <span className="text-sm text-gray-600">
          Showing {startIndex}-{endIndex} of {totalVendors}
        </span>
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 1}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-sm font-medium text-gray-700">
            Page {currentPage} of {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Export Button */}
      <Button
        variant="outline"
        size="sm"
        onClick={onExportCSV}
        className="flex items-center space-x-2"
      >
        <Download className="h-4 w-4" />
        <span>Export CSV</span>
      </Button>
    </div>
  );
}

export default memo(VendorToolbar);
